import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';
import moment from 'moment';

interface VolumeManagerFormState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    document: any;
    form: any;
    washings: any[];
    targetForDelete: any[];
    schema: any;
    schema_washings: any;

    meta: any;
    meta_washings: any;
}

const state: VolumeManagerFormState = {
    loading: false,
    loaded: false,
    fatalError: false,

    document: null,
    form: null,
    washings: [],
    targetForDelete: [],
    schema: null,
    schema_washings: null,

    meta: {},
    meta_washings: {},
};

const initState: VolumeManagerFormState = cloneDeep(state);

export const VOLUME_MANAGER_DATA = 'GET_VOLUME_MANAGER_DATA';
export const VOLUME_MANAGER_SCHEMA = 'GET_VOLUME_MANAGER_SCHEMA';
export const VOLUME_WASHINGS_DATA = 'GET_VOLUME_WASHINGS_DATA';
export const VOLUME_WASHINGS_SCHEMA = 'GET_VOLUME_WASHINGS_SCHEMA';

export const actions: ActionTree<VolumeManagerFormState, RootState> = {
    reset({ commit }) {
        commit('RESET_STATE')
    },

    init({ commit, dispatch }, params) {
        return dispatch('loadData', params);
    },

    loadData({ commit }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(VOLUME_MANAGER_DATA)
                    .raw('documents.get')
                    .action({
                        id: params.id
                    }),
                vm.$q(VOLUME_WASHINGS_DATA)
                    .raw('documents.list')
                    .action({
                        columns: {
                            id: 'vvw.id',
                            volume_id: 'vvw.volume_id',
                            washing_id: 'vvw.washing_id',
                            delay: 'vvw.delay',
                            periodicity: 'vvw.periodicity',
                            periodicity_value: 'vvw.periodicity_value',
                            created_at: 'vvw.created_at',
                            updated_at: 'vvw.updated_at',
                            water_volume: 'vvw.water_volume',
                            detergent_volume: 'vvw.detergent_volume',
                            detergent_concentration: 'vvw.detergent_concentration',
                        },
                        from: 's:voc_volumes_washings',
                        from_as: 'vvw',
                        where: {
                            $eq: ['vvw.volume_id', {$: params.id}]
                        }
                    }),
                vm.$q(VOLUME_WASHINGS_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:voc_volumes_washings`
                    }),
            ]).then((result: ServerResult) => {
                commit('SET_FORM_DATA', {
                    document: result.getOperation(VOLUME_MANAGER_DATA).getContent().document,
                    schema: result.getOperation(VOLUME_MANAGER_DATA).getContent().schema,
                    washings: result.getOperation(VOLUME_WASHINGS_DATA).getContent().documents,
                    schema_washings: result.getOperation(VOLUME_WASHINGS_SCHEMA).getContent().schema,
                    
                });

                commit('LOADED_SUCCESS');
                resolve(null);
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    prepareNew({ commit, state }, initParams) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            vm.$db([
                vm.$q(VOLUME_MANAGER_SCHEMA)
                    .raw('schemas.get')
                    .action({
                        id: `s:voc_volumes`
                    }),
                    vm.$q(VOLUME_WASHINGS_SCHEMA)
                        .raw('schemas.get')
                        .action({
                            id: `s:voc_volumes_washings`
                        }),
            ]).then((result: ServerResult) => {
                commit('GENERATE_FORM_DATA', {
                    ...result.getOperation(VOLUME_MANAGER_SCHEMA).getContent(),
                    schema_washings: result.getOperation(VOLUME_WASHINGS_SCHEMA).getContent().schema,
                    initParams
                });

                commit('LOADED_SUCCESS');
                resolve();
            })
            .catch((error: any) => {
                console.log('error', error)
                commit('LOADED_ERROR');
                reject()
            })
        })
    },

    saveForm({ commit }, documentId) {
        const vm = (this as any)._vm;

        const normalizedForm = prepareFormObjectForSave(state.schema, state.form);
        const washings = state.washings.map((item: any) => {
            return prepareFormObjectForSave(state.schema_washings, item);
        })

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            let saveQuery = null;

            if (documentId) {
                saveQuery = vm.$q('VOLUME_MANAGER_DATA_SAVE')
                    .update(documentId, normalizedForm)
                    .createManyRelation('voc_volumes_washings', 'volume_id', (createRelated: any) => {
                      return washings.filter((item: any) => !item.id || (typeof (item.id) !== 'string')).map((item, index) => createRelated(item));
                    })
                    .updateManyRelation('voc_volumes_washings', 'volume_id', (updateRelated: any) => {
                      return washings.filter((item: any) => (typeof (item.id) == 'string')).map((item, index) => {
                        return updateRelated(item)})
                    })
                    .deleteManyRelation('voc_volumes_washings', 'volume_id', (deleteRelated: any) => {
                      return state.targetForDelete.map((item: any, index) => deleteRelated({id: item}));
                    })
            } else {
                saveQuery = vm.$q('VOLUME_MANAGER_DATA_SAVE')
                    .create('s:voc_volumes', normalizedForm)
                    .withManyRelation('voc_volumes_washings', 'volume_id', (createRelated: any) => {
                      return washings.map((item: any) => createRelated(item));
                    })
            }

            vm.$db([
                saveQuery
            ]).then((result: ServerResult) => {
                const operationResult = result.getOperation('VOLUME_MANAGER_DATA_SAVE');

                if (operationResult.hasError()) {
                    console.log('error', operationResult.getContent());

                    commit('LOADED_SUCCESS');
                    reject();
                } else {
                    commit('ON_FORM_SAVE', operationResult.getContent())

                    commit('LOADED_SUCCESS');
                    resolve(operationResult.getContent());
                }
            })
            .catch((error: any) => {
                console.error('errror', error)
                reject(error);
            });
        })
    },

    changeFormValue({commit}, {field, newValue}) {
        commit('CHANGE_VALUE', {field, newValue});
    },

    addWashing({commit}) {
        commit('ADD_WASHING')
    },

    removeWashing({commit}, index) {
        commit('REMOVE_WASHING', {index})
    },

    setWashingValue({commit}, {index, field, newValue}) {
        console.log(index, field, newValue)
        commit('WASHING_CHANGE_VALUE', {index, field, newValue});
    },
};

export const mutations: MutationTree<VolumeManagerFormState> = {
    RESET_STATE: (state) => {
        for(const key in initState) {
            (state as any)[key] = (initState as any)[key]
        }
    },

    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_VOLUME_MANAGER_DATA(state, {document}) {
        state.document = document;
    },

    SET_FORM_DATA(state, {document, schema, washings, schema_washings}) {
        state.document = document;
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);
        state.meta_washings = fetchEnumsFromSchema(schema_washings);

        state.form = generateFormObject(schema, state.document);

        state.schema_washings = schema_washings
        state.washings = washings.map((item: any) => {
            console.log(item, generateFormObject(schema_washings, item))
            return generateFormObject(schema_washings, item)
        })
    },

    GENERATE_FORM_DATA(state, {schema, initParams}) {
        state.document = {};
        state.schema = schema;
        state.meta = fetchEnumsFromSchema(schema);

        state.form = generateFormObject(schema, initParams);
    },

    ON_FORM_SAVE(state, {document, schema}) {
        state.document = document;
        state.schema = schema;

        state.form = generateFormObject(schema, state.document);
    },

    CHANGE_VALUE(state: VolumeManagerFormState, {field, newValue}) {
        switch (field) {
            default:
                state.form[field] = newValue;
        }
    },

    WASHING_CHANGE_VALUE(state: VolumeManagerFormState, {index, field, newValue}) {
        console.log(index, field, newValue, moment(newValue).utc().local().format('HH:mm:ss'))
        switch (field) {
            // case 'delay':
            //     newValue = moment.duration(moment(newValue).utc().local().format('HH:mm:ss')).asSeconds()
            //     state.washings[index][field] = {'months': 0, 'days': 0, 'secs': newValue};
            //     break;

            default:
                state.washings[index][field] = newValue;
        }
    },

    ADD_WASHING(state: VolumeManagerFormState) {
        state.washings.push({
            volume_id: state.form.id,
            washing_id: null,
            delay: null,
            periodicity: null,
            periodicity_value: null,
            water_volume: null,
            detergent_volume: null,
            detergent_concentration: null,
        })
    },

    REMOVE_WASHING(state: VolumeManagerFormState, {index}) {
        if (state.washings[index].id) {
            state.targetForDelete.push(state.washings[index].id)
          }
          state.washings.splice(index, 1)
    }
};

const getters = {
    fields: (state: VolumeManagerFormState) => {
        return state.schema.fields.reduce((prev: any, field: any) => {
            prev[field.code] = field;
            return prev;
        }, {});
    },
};

export const STORE_KEY = '$_VOLUME_manager_form';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
